import { useEffect, useState, type ReactNode } from "react";
import { documentStore, selectionStore, uiStore, viewStore } from "@/editor/store";
import { useStoreSubscribe } from "@/lib/store";
import { LocateFixed, Play } from "lucide-react";
import type { CurvaDocument } from "@/document/model/document";
import { findSceneNode, getAllSceneNodes, getSceneNodeChildren } from "@/document/model/document";
import { framesWithOnlyOutgoingPrototypeLinks } from "@/document/model/prototype";
import { flexChildLayout, frameAutoLayoutMode, frameCornerRadii, nodeConstraints, type AutoLayoutMode, type ComponentInstanceNode, type ComponentNode, type FlexChildLayout, type FrameFlexLayout, type FrameMirrorLayout, type FrameNode, type FrameRepeatLayout, type PrimitiveNode, type SceneNode } from "@/document/model/nodes";
import type { NodeConstraints } from "@/document/model/constraints";
import { componentVariants } from "@/document/operations/componentSystem";
import type { OvalPrimitive, PolygonPrimitive, PrimitiveDefinition, RectanglePrimitive, StarPrimitive } from "@/document/model/primitives";
import type { DropShadowEffect, InnerShadowEffect, NodeStyle, Paint } from "@/document/model/styles";
import { colorAtGradientPosition, createSolidPaint, normalizeStrokeStack } from "@/document/model/styles";
import type { OklchColor } from "@/document/model/color";
import type { RichTextModel } from "@/document/model/text";
import type { TypeStyle } from "@/document/model/text";
import { textInspectorRange } from "@/document/model/text";
import type { VectorHandleMode, VectorStrokeJoin } from "@/document/model/vectorNetwork";
import { getSelectionBounds } from "@/editor/tools/hitTest";
import type { Rect } from "@/lib/math/rect";
import { DevPanel, DevPanelEmpty } from "./right-panel/DevPanel";
import { ScriptsPanel } from "./right-panel/ScriptsPanel";
import { AlignmentSection } from "./right-panel/AlignmentSection";
import { getAlignmentReferenceBounds } from "./right-panel/alignment";
import { BoundsInspector } from "./right-panel/BoundsInspector";
import { ConstraintsSection } from "./right-panel/ConstraintsSection";
import { ColorPickerPopover } from "./right-panel/ColorPickerPopover";
import { EffectEditorPopover } from "./right-panel/EffectEditorPopover";
import { effectTypeLabel } from "./right-panel/effectLabels";
import { EffectsSection } from "./right-panel/EffectsSection";
import type { CreateStyleFromSelectionOptions, DocumentStyle, DocumentStyleKind } from "@/document/model/documentStyles";
import { resolveFillPaints, resolveStrokeStyles, styleBindingId } from "@/document/model/documentStyles";
import { AppliedStyleRow } from "./right-panel/AppliedStyleRow";
import { CreateStyleDialog } from "./right-panel/CreateStyleDialog";
import { StyleEditorPopover } from "./right-panel/StyleEditorPopover";
import { StylePickerPopover } from "./right-panel/StylePickerPopover";
import { Metric } from "./right-panel/Metric";
import { PanelLabeledSelect } from "./right-panel/PanelSelect";
import { LayoutSection } from "./right-panel/LayoutSection";
import { INSPECTOR_SECTIONS_CLASS, PanelSection, PanelSectionAddButton, PanelSectionHeaderActions } from "./right-panel/PanelSection";
import { PaintSection, FillPaintStack } from "./right-panel/PaintSection";
import { SelectionColorsSection } from "./right-panel/SelectionColorsSection";
import { type SelectionPaintKind } from "./right-panel/selectionPaintPicker";
import {
  paintForTarget,
  paintWithOpacity,
  resolvePaintTarget,
  styleWithAddedFill,
  styleWithAddedStroke,
  styleWithDuplicatedFill,
  styleWithPaint,
  styleWithReorderedFill,
  styleWithStrokeAlignAt,
  styleWithStrokeWidthAt,
  styleWithUpdatedEffect,
  styleWithoutFill,
  styleWithoutStroke,
} from "./right-panel/paintStyles";
import { TypographySection } from "./right-panel/TypographySection";
import type { PaintTarget } from "./right-panel/types";
import type { PickerMode } from "./right-panel/types";
import { AppearanceSection } from "./right-panel/AppearanceSection";
import {
  documentBoundsFromInspectorBounds,
  inspectorBoundsFromDocumentBounds,
  parentFrameContextForNode,
  sharedNearestParentFrameContext,
} from "./right-panel/boundsFrameContext";
import type { ColorSamplerRequestWithCommit } from "./colorSampler";
import type { FrameTemplate, FrameTemplateCategory } from "./frameTemplates";
import { POPULAR_CATEGORY_ID } from "./frameTemplates";

export type { FrameTemplate } from "./frameTemplates";

const FILL_REORDER_MIME = "application/x-curva-fill-index";

interface RightPanelProps {
  frameTemplateCategories: FrameTemplateCategory[];
  onBoundsChange: (nodeId: string, bounds: Rect) => void;
  onSelectedNodeBoundsChange: (updates: Array<{ nodeId: string; bounds: Rect }>, label: string) => void;
  onSelectionBoundsChange: (bounds: Rect) => void;
  onSelectionRotationChange: (rotation: number) => void;
  onStyleChange: (nodeId: string, style: NodeStyle) => void;
  onSelectedNodeStylesChange: (updates: Array<{ nodeId: string; style: NodeStyle }>, label: string) => void;
  onSelectionPaintChange: (nodeIds: string[], kind: SelectionPaintKind, paint: Paint) => void;
  onPrimitiveChange: (nodeId: string, primitive: PrimitiveDefinition) => void;
  onTextChange: (nodeId: string, text: RichTextModel) => void;
  onTextStylePatch: (nodeId: string, patch: Partial<TypeStyle>) => void;
  onTextStylePreview?: (nodeId: string, patch: Partial<TypeStyle>) => void;
  onTextStylePreviewClear?: (nodeId?: string) => void;
  textFillForInspector: (nodeId: string) => Paint | "mixed" | undefined;
  onFrameClipsContentChange: (nodeId: string, clipsContent: boolean) => void;
  onFrameCornerRadiiChange: (nodeId: string, cornerRadii: [number, number, number, number]) => void;
  onFrameLayoutChange: (nodeId: string, patch: {
    autoLayout?: AutoLayoutMode;
    flexLayout?: FrameFlexLayout;
    repeatLayout?: FrameRepeatLayout;
    mirrorLayout?: FrameMirrorLayout;
  }) => void;
  onFlexChildLayoutChange: (nodeId: string, layout: FlexChildLayout) => void;
  onConstraintsChange: (nodeId: string, constraints: NodeConstraints) => void;
  onAddComponentVariant: (componentId: string) => void;
  onDeleteComponentVariant: (componentId: string, variantId: string) => void;
  onInstanceVariantChange: (instanceId: string, variantId: string) => void;
  onFrameTemplateInsert: (template: FrameTemplate) => void;
  onColorSampleRequest: (request: ColorSamplerRequestWithCommit) => void;
  onColorSampleModeChange: (sourceId: string, mode: PickerMode) => void;
  onColorSampleCancel: (sourceId?: string) => void;
  onVectorVertexSettingsChange: (
    nodeId: string,
    vertexIds: string[],
    patch: { handleMode?: VectorHandleMode; strokeJoin?: VectorStrokeJoin; cornerRadius?: number },
    anchorHandle?: { edgeId: string; endpoint: "from" | "to" },
  ) => void;
  onReplaceSelectionPaint: (scope: "fill" | "stroke" | "all", fromPaint: Paint, toPaint: Paint) => void;
  onCreateDocumentStyle: (nodeId: string, kind: DocumentStyleKind, name: string, options?: CreateStyleFromSelectionOptions) => string | undefined;
  onApplyDocumentStyle: (nodeId: string, styleId: string) => void;
  onDetachDocumentStyle: (nodeId: string, kind: DocumentStyleKind) => void;
  onUpdateDocumentStyle: (style: DocumentStyle) => void;
  onDeleteDocumentStyle: (styleId: string) => void;
  onLocateNode: (nodeId: string) => void;
  onPlayPrototype: (frameId: string) => void;
  allowScripts?: boolean;
  onCreateDocumentScript?: (parentFolderId?: string) => string | undefined;
  onCreateScriptFolder?: () => string | undefined;
  onRenameScriptEntry?: (entryId: string, name: string) => void;
  onDuplicateDocumentScript?: (scriptId: string) => string | undefined;
  onDeleteScriptEntry?: (entryId: string) => void;
  onMoveScriptEntry?: (entryId: string, parentFolderId: string | undefined, index: number) => void;
  onRunDocumentScript?: (scriptId: string) => void | Promise<void>;
  onRunEditorScript?: (scriptId: string) => void | Promise<void>;
  onCopyEditorScriptToDocument?: (entryId: string) => void;
}

export function RightPanel({
  frameTemplateCategories,
  onBoundsChange,
  onSelectedNodeBoundsChange,
  onSelectionBoundsChange,
  onSelectionRotationChange,
  onStyleChange,
  onSelectedNodeStylesChange,
  onSelectionPaintChange,
  onPrimitiveChange,
  onTextChange,
  onTextStylePatch,
  onTextStylePreview,
  onTextStylePreviewClear,
  textFillForInspector,
  onFrameClipsContentChange,
  onFrameCornerRadiiChange,
  onFrameLayoutChange,
  onFlexChildLayoutChange,
  onConstraintsChange,
  onAddComponentVariant,
  onDeleteComponentVariant,
  onInstanceVariantChange,
  onFrameTemplateInsert,
  onColorSampleRequest,
  onColorSampleModeChange,
  onColorSampleCancel,
  onVectorVertexSettingsChange,
  onReplaceSelectionPaint,
  onCreateDocumentStyle,
  onApplyDocumentStyle,
  onDetachDocumentStyle,
  onUpdateDocumentStyle,
  onDeleteDocumentStyle,
  onLocateNode,
  onPlayPrototype,
  allowScripts = false,
  onCreateDocumentScript,
  onCreateScriptFolder,
  onRenameScriptEntry,
  onDuplicateDocumentScript,
  onDeleteScriptEntry,
  onMoveScriptEntry,
  onRunDocumentScript,
  onRunEditorScript,
  onCopyEditorScriptToDocument,
}: RightPanelProps) {
  const document = useStoreSubscribe(documentStore.document);
  const selection = useStoreSubscribe(selectionStore.selection);
  const activeTool = useStoreSubscribe(selectionStore.tool).activeTool;
  const activeTab = useStoreSubscribe(uiStore.rightPanelTab);
  const colorSampler = useStoreSubscribe(uiStore.colorSampler);
  const textSelectionState = useStoreSubscribe(uiStore.textSelection);
  const boundsPreview = useStoreSubscribe(viewStore.boundsPreview);
  const selectedNodeIds = selection.selectedNodeIds;
  const primarySelectedNodeId = selection.primarySelectedNodeId;
  const mode = selection.mode;
  const selectedVertexIds = selection.selectedVertexIds ?? [];
  const selectedHandle = selection.selectedHandle;
  const selectionBounds = getSelectionBounds(document, selectedNodeIds);
  const activeColorSamplerSourceId = colorSampler?.sourceId;
  const textEditSelection = mode === "textEdit" && textSelectionState?.nodeId === primarySelectedNodeId
    ? { anchor: textSelectionState!.anchor, focus: textSelectionState!.focus }
    : undefined;
  const nodes = getAllSceneNodes(document);
  const node = nodes.find(candidate => candidate.id === primarySelectedNodeId);
  const hasSelection = selectedNodeIds.length > 0;
  const selectedNodes = nodes.filter(candidate => selectedNodeIds.includes(candidate.id));
  const selectedFrame = selectedNodeIds.length === 1 && node?.type === "frame" ? node : undefined;
  const selectedComponent = selectedNodeIds.length === 1 && node?.type === "component" ? node : undefined;
  const selectedInstance = selectedNodeIds.length === 1 && node?.type === "instance" ? node : undefined;
  const selectedInstanceComponent = selectedInstance ? nodes.find((candidate): candidate is ComponentNode => candidate.type === "component" && candidate.id === selectedInstance.componentId) : undefined;
  const selectedText = selectedNodeIds.length === 1 && node?.type === "text" ? node : undefined;
  const selectedRectangle = selectedNodeIds.length === 1 && isRectangleNode(node) ? node : undefined;
  const selectedOval = selectedNodeIds.length === 1 && isOvalNode(node) ? node : undefined;
  const selectedPolygon = selectedNodeIds.length === 1 && isPolygonNode(node) ? node : undefined;
  const selectedStar = selectedNodeIds.length === 1 && isStarNode(node) ? node : undefined;
  const parentFlexFrame = node && selectedNodeIds.length === 1 ? directParentFlexFrame(document, node.id) : undefined;
  const parentFrameContext = node && selectedNodeIds.length === 1 ? parentFrameContextForNode(document, node.id) : undefined;
  const parentFrame = parentFrameContext
    ? (() => {
        const candidate = findSceneNode(document, parentFrameContext.frameId);
        return candidate?.type === "frame" ? candidate : undefined;
      })()
    : undefined;
  const selectedFlexChildLayout = node && parentFlexFrame ? flexChildLayout(node) : undefined;
  const selectedVectorVertices = mode === "vectorEdit" && node?.type === "vectorNetwork"
    ? selectedVertexIds.length > 0
      ? node.network.vertices.filter(vertex => selectedVertexIds.includes(vertex.id))
      : selectedHandle
        ? (() => {
            const edge = node.network.edges.find(candidate => candidate.id === selectedHandle.edgeId);
            const vertexId = edge?.[selectedHandle.endpoint].vertexId;
            const vertex = vertexId ? node.network.vertices.find(candidate => candidate.id === vertexId) : undefined;
            return vertex ? [vertex] : [];
          })()
        : []
    : [];
  const vertexDirectlySelected = selectedVertexIds.length > 0;
  const editingDocumentBounds = selectedNodeIds.length > 1 ? boundsPreview ?? selectionBounds : boundsPreview ?? node?.bounds;
  const boundsFrameContext = sharedNearestParentFrameContext(document, selectedNodeIds);
  const alignmentReferenceBounds = getAlignmentReferenceBounds(document, selectedNodeIds, selectionBounds);
  const editingBounds = editingDocumentBounds
    ? inspectorBoundsFromDocumentBounds(editingDocumentBounds, boundsFrameContext)
    : undefined;
  const editingRotation = selectedNodeIds.length > 1 ? sharedRotation(selectedNodes) : node?.transform.rotation;
  const editingOpacity = selectedNodes.length > 0 ? sharedOpacity(selectedNodes) : undefined;
  const [pickerTarget, setPickerTarget] = useState<ColorPickerTarget | undefined>(undefined);
  const [editingEffectId, setEditingEffectId] = useState<string | undefined>(undefined);
  const [stylePickerKind, setStylePickerKind] = useState<DocumentStyleKind | undefined>(undefined);
  const [createStyleKind, setCreateStyleKind] = useState<DocumentStyleKind | undefined>(undefined);
  const [createStyleOptions, setCreateStyleOptions] = useState<CreateStyleFromSelectionOptions>({});
  const [editingStyleId, setEditingStyleId] = useState<string | undefined>(undefined);
  const pickerPaintTarget = isPaintPickerTarget(pickerTarget) ? pickerTarget : undefined;
  const resolvedPickerPaintTarget = pickerPaintTarget ? resolvePaintTarget(pickerPaintTarget) : undefined;
  const pickerEffect = isEffectPickerTarget(pickerTarget) && node
    ? node.style.effects?.find((effect): effect is DropShadowEffect | InnerShadowEffect => effect.id === pickerTarget.effectId && (effect.type === "dropShadow" || effect.type === "innerShadow"))
    : undefined;
  const editingEffect = editingEffectId && node
    ? node.style.effects?.find(effect => effect.id === editingEffectId)
    : undefined;
  const displayedFillInspector = selectedText ? textFillForInspector(selectedText.id) : undefined;
  const displayedFillMixed = displayedFillInspector === "mixed";
  const displayedFillPaint = displayedFillMixed ? undefined : displayedFillInspector ?? node?.style.fills.at(-1);
  const fillStyleId = node ? styleBindingId(node, "fill") : undefined;
  const strokeStyleId = node ? styleBindingId(node, "stroke") : undefined;
  const effectsStyleId = node ? styleBindingId(node, "effects") : undefined;
  const textStyleId = node ? styleBindingId(node, "text") : undefined;
  const resolvedFills = node ? resolveFillPaints(document, node) : [];
  const resolvedStrokes = node ? resolveStrokeStyles(document, node) : [];
  const activePaint = pickerTarget && node
    ? pickerEffect
      ? createSolidPaint(pickerEffect.color)
      : selectedText && resolvedPickerPaintTarget?.type === "fill"
      ? (() => {
          const fill = textFillForInspector(selectedText.id);
          return fill === "mixed" ? createSolidPaint("#f8fafc") : fill ?? createSolidPaint("#f8fafc");
        })()
      : pickerPaintTarget
        ? paintForTarget(node.style, pickerPaintTarget) ?? createSolidPaint("#f8fafc")
        : undefined
    : undefined;
  const [templatesOpen, setTemplatesOpen] = useState(true);
  const [openTemplateCategories, setOpenTemplateCategories] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(frameTemplateCategories.map(category => [category.id, category.id === POPULAR_CATEGORY_ID])),
  );

  useEffect(() => {
    setOpenTemplateCategories(current => {
      const next = { ...current };
      for (const category of frameTemplateCategories) {
        if (next[category.id] === undefined) {
          next[category.id] = category.id === POPULAR_CATEGORY_ID;
        }
      }
      return next;
    });
  }, [frameTemplateCategories]);

  useEffect(() => {
    if (!node) {
      setPickerTarget(undefined);
      onColorSampleCancel();
    }
  }, [node, onColorSampleCancel]);

  const selectionPaintPickerToken = useStoreSubscribe(uiStore.selectionPaintPicker)?.token;
  useEffect(() => {
    if (selectionPaintPickerToken !== undefined) {
      setPickerTarget(undefined);
    }
  }, [selectionPaintPickerToken]);

  useEffect(() => {
    setEditingEffectId(undefined);
  }, [node?.id]);

  useEffect(() => {
    if (editingEffectId && node && !node.style.effects?.some(effect => effect.id === editingEffectId)) {
      setEditingEffectId(undefined);
    }
  }, [node, editingEffectId]);

  useEffect(() => {
    const editingImageFillTarget = node && pickerPaintTarget && resolvedPickerPaintTarget?.type === "fill" && activePaint?.type === "image"
      ? { nodeId: node.id, target: pickerPaintTarget }
      : undefined;
    uiStore.setEditingImageFillTarget(editingImageFillTarget);
    return () => uiStore.setEditingImageFillTarget(undefined);
  }, [activePaint?.type, node?.id, pickerPaintTarget, resolvedPickerPaintTarget?.type]);

  useEffect(() => {
    const editingGradientFillTarget =
      node &&
      pickerPaintTarget &&
      (resolvedPickerPaintTarget?.type === "fill" || resolvedPickerPaintTarget?.type === "stroke") &&
      (activePaint?.type === "linearGradient" || activePaint?.type === "radialGradient")
        ? { nodeId: node.id, target: pickerPaintTarget }
        : undefined;
    uiStore.setEditingGradientFillTarget(editingGradientFillTarget);
    return () => uiStore.setEditingGradientFillTarget(undefined);
  }, [activePaint?.type, node?.id, pickerPaintTarget, resolvedPickerPaintTarget?.type]);

  const prototypeFrames = framesWithOnlyOutgoingPrototypeLinks(document);

  return (
    <aside className="relative flex h-full min-h-0 w-72 shrink-0 flex-col border-l border-white/10 bg-[#191d22] text-[12px] text-slate-300">
      <div className="shrink-0 border-b border-white/10 p-2">
        <div className="grid grid-cols-4 rounded bg-white/[0.04] p-0.5">
          {(["design", "prototype", "dev", "scripts"] as const).map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => uiStore.setRightPanelTab(tab)}
              className={`h-7 rounded text-[11px] font-medium capitalize transition-colors ${
                activeTab === tab ? "bg-sky-500/20 text-sky-100" : "text-slate-400 hover:bg-white/[0.07] hover:text-slate-200"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
      </div>
      {activeTab === "scripts" ? (
        <ScriptsPanel
          allowScripts={allowScripts}
          onCreateScript={parentFolderId => onCreateDocumentScript?.(parentFolderId)}
          onCreateFolder={() => onCreateScriptFolder?.()}
          onRenameEntry={(entryId, name) => onRenameScriptEntry?.(entryId, name)}
          onDuplicateScript={scriptId => onDuplicateDocumentScript?.(scriptId)}
          onDeleteEntry={entryId => onDeleteScriptEntry?.(entryId)}
          onMoveEntry={(entryId, target) => onMoveScriptEntry?.(entryId, target.parentFolderId, target.index)}
          onRunScript={scriptId => onRunDocumentScript?.(scriptId)}
          onRunEditorScript={scriptId => onRunEditorScript?.(scriptId)}
          onCopyEditorScriptToDocument={entryId => onCopyEditorScriptToDocument?.(entryId)}
        />
      ) : activeTab === "dev" ? (
        node ? (
          <DevPanel node={node} document={document} />
        ) : (
          <DevPanelEmpty />
        )
      ) : activeTab === "prototype" ? (
        <PrototypePanel
          selectedFrame={selectedFrame}
          outgoingOnlyFrames={prototypeFrames}
          onLocateNode={onLocateNode}
          onPlayPrototype={onPlayPrototype}
        />
      ) : activeTool === "frame" ? (
        <div className="flex min-h-0 flex-1 flex-col p-3">
          <button
            type="button"
            onClick={() => setTemplatesOpen(open => !open)}
            className="mb-2 flex shrink-0 w-full items-center justify-between text-left"
            aria-expanded={templatesOpen}
          >
            <span className="text-[11px] uppercase text-slate-500">Frame Templates</span>
            <span className="text-[11px] text-slate-500">{templatesOpen ? "Hide" : "Show"}</span>
          </button>
          {templatesOpen ? (
            <div className="min-h-0 flex-1 space-y-2 overflow-y-auto overscroll-contain pr-1">
              {frameTemplateCategories.map(category => {
                const categoryOpen = openTemplateCategories[category.id] ?? true;
                return (
                  <div key={category.id}>
                    <button
                      type="button"
                      onClick={() => setOpenTemplateCategories(current => ({ ...current, [category.id]: !categoryOpen }))}
                      className="flex w-full items-center justify-between rounded px-2 py-1 text-left text-[11px] font-medium text-slate-400 transition-colors hover:bg-white/5 hover:text-slate-200"
                      aria-expanded={categoryOpen}
                    >
                      <span>{category.name}</span>
                      <span className="text-slate-500">{categoryOpen ? "−" : "+"}</span>
                    </button>
                    {categoryOpen ? (
                      <div className="mt-1 grid grid-cols-1 gap-0.5">
                        {category.templates.map(template => (
                          <button
                            key={template.id}
                            type="button"
                            onClick={() => onFrameTemplateInsert(template)}
                            className="flex min-h-8 items-center justify-between rounded px-2 py-1 text-left text-slate-300 transition-colors hover:bg-sky-500/15 hover:text-sky-100 active:bg-sky-500/20"
                          >
                            <span className="min-w-0 truncate pr-2">{template.name}</span>
                            <span className="shrink-0 font-mono text-[10px] text-slate-500">{template.width}×{template.height}</span>
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      ) : (
      <div className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain p-3 ${INSPECTOR_SECTIONS_CLASS}`}>
        {hasSelection ? (
          <BoundsInspector
            selectedCount={selectedNodeIds.length}
            bounds={editingBounds}
            rotation={editingRotation}
            fillControls={node && selectedFlexChildLayout ? {
              width: {
                enabled: selectedFlexChildLayout.fillWidth,
                onChange: fillWidth => onFlexChildLayoutChange(node.id, { ...selectedFlexChildLayout, fillWidth }),
              },
              height: {
                enabled: selectedFlexChildLayout.fillHeight,
                onChange: fillHeight => onFlexChildLayoutChange(node.id, { ...selectedFlexChildLayout, fillHeight }),
              },
            } : undefined}
            onBoundsCommit={commitBounds}
            onRotationCommit={onSelectionRotationChange}
          />
        ) : null}

        {node && selectedNodeIds.length === 1 && parentFrame ? (
          <ConstraintsSection
            constraints={nodeConstraints(node)}
            parentFrame={parentFrame}
            onChange={constraints => onConstraintsChange(node.id, constraints)}
          />
        ) : null}

        {node && selectedNodeIds.length === 1 ? (
          <LayoutSection
            selectedNode={node}
            parentFlexFrame={parentFlexFrame}
            onFrameLayoutChange={onFrameLayoutChange}
            onFlexChildLayoutChange={onFlexChildLayoutChange}
          />
        ) : null}

        {editingOpacity !== undefined ? (
          <AppearanceSection
            opacity={editingOpacity}
            disabled={false}
            onOpacityCommit={opacity => {
              const updates = selectedNodes.map(selectedNode => ({
                nodeId: selectedNode.id,
                style: { ...selectedNode.style, opacity },
              }));
              onSelectedNodeStylesChange(updates, selectedNodes.length > 1 ? "Updated opacity" : "Updated style");
            }}
          />
        ) : null}

        {selectedNodeIds.length > 0 ? (
          <AlignmentSection
            nodes={selectedNodes}
            referenceBounds={alignmentReferenceBounds}
            onBoundsChange={onSelectedNodeBoundsChange}
          />
        ) : null}

        {selectedFrame ? (
          <FrameSection
            frame={selectedFrame}
            onClipsContentChange={onFrameClipsContentChange}
            onCornerRadiiChange={onFrameCornerRadiiChange}
          />
        ) : null}

        {selectedComponent ? (
          <ComponentSection
            component={selectedComponent}
            onAddVariant={onAddComponentVariant}
            onDeleteVariant={onDeleteComponentVariant}
          />
        ) : null}

        {selectedInstance && selectedInstanceComponent ? (
          <InstanceSection
            instance={selectedInstance}
            component={selectedInstanceComponent}
            onVariantChange={onInstanceVariantChange}
          />
        ) : null}

        {selectedRectangle ? (
          <RectangleSection
            node={selectedRectangle}
            onPrimitiveChange={onPrimitiveChange}
          />
        ) : null}

        {selectedOval ? (
          <OvalSection
            node={selectedOval}
            onPrimitiveChange={onPrimitiveChange}
          />
        ) : null}

        {selectedPolygon ? (
          <PolygonSection
            node={selectedPolygon}
            onPrimitiveChange={onPrimitiveChange}
          />
        ) : null}

        {selectedStar ? (
          <StarSection
            node={selectedStar}
            onPrimitiveChange={onPrimitiveChange}
          />
        ) : null}

        {selectedText ? (
          <TypographySection
            text={selectedText.text}
            styleRange={textInspectorRange(selectedText.text, textEditSelection)}
            onChange={text => onTextChange(selectedText.id, text)}
            onStylePatch={patch => onTextStylePatch(selectedText.id, patch)}
            onStylePreview={onTextStylePreview ? (patch => onTextStylePreview(selectedText.id, patch)) : undefined}
            onStylePreviewClear={onTextStylePreviewClear ? (() => onTextStylePreviewClear(selectedText.id)) : undefined}
            appliedStyleId={textStyleId}
            onOpenStyles={() => openStylePicker("text")}
            onDetachStyle={() => onDetachDocumentStyle(selectedText.id, "text")}
            onEditStyle={textStyleId ? () => setEditingStyleId(textStyleId) : undefined}
            document={document}
          />
        ) : null}

        {node?.type === "vectorNetwork" && selectedVectorVertices.length > 0 ? (
          <VectorSection
            vertices={selectedVectorVertices}
            onChange={patch => onVectorVertexSettingsChange(
              node.id,
              selectedVectorVertices.map(vertex => vertex.id),
              patch,
              vertexDirectlySelected ? undefined : selectedHandle,
            )}
          />
        ) : null}

        {node ? (
          <>
            {selectedText ? (
              <PaintSection
                title="Fill"
                disabled={false}
                paint={displayedFillPaint}
                mixed={displayedFillMixed}
                appliedStyle={fillStyleId ? (
                  <AppliedStyleRow
                    document={document}
                    styleId={fillStyleId}
                    kind="fill"
                    onDetach={() => onDetachDocumentStyle(node.id, "fill")}
                    onEdit={() => setEditingStyleId(fillStyleId)}
                  />
                ) : undefined}
                onOpenStyles={() => openStylePicker("fill", { fromTextFill: true })}
                onColorClick={() => {
                  if (!displayedFillPaint) {
                    onTextStylePatch(selectedText.id, { fill: createSolidPaint("#f8fafc") });
                  }
                  setPickerTarget({ type: "fill", index: 0 });
                }}
                onAdd={() => onTextStylePatch(selectedText.id, { fill: createSolidPaint("#f8fafc") })}
                onRemove={() => {
                  onTextStylePatch(selectedText.id, { fill: undefined });
                  onStyleChange(node.id, styleWithoutFill(node.style));
                  if (resolvedPickerPaintTarget?.type === "fill") {
                    setPickerTarget(undefined);
                  }
                }}
                onOpacityCommit={opacity => {
                  const nextPaint = paintWithOpacity(displayedFillPaint, opacity);
                  if (nextPaint) {
                    onTextStylePatch(selectedText.id, { fill: nextPaint });
                  }
                }}
                onColorCommit={color => {
                  onTextStylePatch(selectedText.id, { fill: createSolidPaint(color) });
                }}
              />
            ) : (
              <PaintStackSection
                title="Fill"
                empty={resolvedFills.length === 0}
                onOpenStyles={resolvedFills.length === 1 && !fillStyleId ? undefined : () => openStylePicker("fill")}
                onAdd={() => onStyleChange(node.id, styleWithAddedFill(node.style))}
                appliedStyle={fillStyleId ? (
                  <AppliedStyleRow
                    document={document}
                    styleId={fillStyleId}
                    kind="fill"
                    onDetach={() => onDetachDocumentStyle(node.id, "fill")}
                    onEdit={() => setEditingStyleId(fillStyleId)}
                  />
                ) : undefined}
              >
                <FillPaintStack
                  fills={resolvedFills}
                  mimeType={FILL_REORDER_MIME}
                  reorderable={!fillStyleId}
                  onReorder={(fromIndex, hoverIndex, placement) => {
                    onStyleChange(node.id, styleWithReorderedFill(node.style, fromIndex, hoverIndex, placement));
                    if (resolvedPickerPaintTarget?.type === "fill") {
                      setPickerTarget(undefined);
                    }
                  }}
                >
                  {({ index, paint, reorder }) => (
                    <PaintSection
                      key={paint.id}
                      title={index === resolvedFills.length - 1 ? "Fill" : `Fill ${index + 1}`}
                      disabled={false}
                      paint={paint}
                      compactTitle
                      hideCompactTitle
                      reorder={reorder}
                      onOpenStyles={resolvedFills.length === 1 ? () => openStylePicker("fill") : undefined}
                      onColorClick={() => setPickerTarget({ type: "fill", index })}
                      onDuplicate={() => {
                        onStyleChange(node.id, styleWithDuplicatedFill(node.style, index));
                      }}
                      onRemove={() => {
                        onStyleChange(node.id, styleWithoutFill(node.style, index));
                        if (resolvedPickerPaintTarget?.type === "fill" && resolvedPickerPaintTarget.index === index) {
                          setPickerTarget(undefined);
                        }
                      }}
                      onOpacityCommit={opacity => {
                        const nextPaint = paintWithOpacity(paint, opacity);
                        if (nextPaint) {
                          onStyleChange(node.id, styleWithPaint(node.style, { type: "fill", index }, nextPaint));
                        }
                      }}
                      onColorCommit={color => {
                        onStyleChange(node.id, styleWithPaint(node.style, { type: "fill", index }, color));
                      }}
                    />
                  )}
                </FillPaintStack>
              </PaintStackSection>
            )}

            <PaintStackSection
              title="Stroke"
              empty={resolvedStrokes.length === 0}
              onOpenStyles={() => openStylePicker("stroke")}
              onAdd={() => onStyleChange(node.id, styleWithAddedStroke(node.style))}
              appliedStyle={strokeStyleId ? (
                <AppliedStyleRow
                  document={document}
                  styleId={strokeStyleId}
                  kind="stroke"
                  onDetach={() => onDetachDocumentStyle(node.id, "stroke")}
                  onEdit={() => setEditingStyleId(strokeStyleId)}
                />
              ) : undefined}
            >
              {resolvedStrokes.map((_, index) => index).reverse().map(index => {
                const stroke = resolvedStrokes[index];
                if (!stroke) {
                  return null;
                }
                return (
                  <PaintSection
                    key={`stroke-${index}`}
                    title={index === resolvedStrokes.length - 1 ? "Stroke" : `Stroke ${index + 1}`}
                    disabled={false}
                    paint={stroke.paint}
                    width={stroke.width}
                    align={stroke.align ?? "inside"}
                    compactTitle
                    onColorClick={() => setPickerTarget({ type: "stroke", index })}
                    onRemove={() => {
                      onStyleChange(node.id, styleWithoutStroke(node.style, index));
                      if (resolvedPickerPaintTarget?.type === "stroke" && resolvedPickerPaintTarget.index === index) {
                        setPickerTarget(undefined);
                      }
                    }}
                    onOpacityCommit={opacity => {
                      const nextPaint = paintWithOpacity(stroke.paint, opacity);
                      if (nextPaint) {
                        onStyleChange(node.id, styleWithPaint(node.style, { type: "stroke", index }, nextPaint));
                      }
                    }}
                    onWidthCommit={width => onStyleChange(node.id, styleWithStrokeWidthAt(node.style, index, width))}
                    onAlignCommit={align => onStyleChange(node.id, styleWithStrokeAlignAt(node.style, index, align))}
                    onColorCommit={color => {
                      onStyleChange(node.id, styleWithPaint(node.style, { type: "stroke", index }, color));
                    }}
                  />
                );
              })}
            </PaintStackSection>

            <EffectsSection
              style={node.style}
              onStyleChange={style => onStyleChange(node.id, style)}
              onEditEffect={setEditingEffectId}
              appliedStyleId={effectsStyleId}
              document={document}
              onOpenStyles={() => openStylePicker("effects")}
              onDetachStyle={() => onDetachDocumentStyle(node.id, "effects")}
              onEditStyle={effectsStyleId ? () => setEditingStyleId(effectsStyleId) : undefined}
            />
          </>
        ) : null}

        <SelectionColorsSection
          document={document}
          selectedNodeIds={selectedNodeIds}
          activeColorSamplerSourceId={activeColorSamplerSourceId}
          onReplacePaint={onReplaceSelectionPaint}
          onColorSampleRequest={onColorSampleRequest}
          onColorSampleModeChange={onColorSampleModeChange}
          onColorSampleCancel={onColorSampleCancel}
        />
      </div>
      )}

      {node && editingEffect ? (
        <EffectEditorPopover
          style={node.style}
          effect={editingEffect}
          suspendDismiss={isEffectPickerTarget(pickerTarget) && pickerTarget.effectId === editingEffect.id}
          onStyleChange={style => onStyleChange(node.id, style)}
          onColorClick={() => setPickerTarget({ type: "effectColor", effectId: editingEffect.id })}
          onClose={() => setEditingEffectId(undefined)}
        />
      ) : null}

      {node && pickerTarget && activePaint ? (
        <ColorPickerPopover
          title={
            isEffectPickerTarget(pickerTarget) && pickerEffect
              ? effectTypeLabel(pickerEffect.type)
              : resolvedPickerPaintTarget?.type === "fill"
                ? "Fill"
                : "Stroke"
          }
          paint={activePaint}
          allowImage={resolvedPickerPaintTarget?.type === "fill" && !selectedText}
          activeColorSamplerSourceId={activeColorSamplerSourceId}
          onColorSampleRequest={onColorSampleRequest}
          onColorSampleModeChange={onColorSampleModeChange}
          onClose={() => {
            setPickerTarget(undefined);
            onColorSampleCancel();
          }}
          onChange={
            isEffectPickerTarget(pickerTarget)
              ? paint => {
                if (paint.type === "solid") {
                  onStyleChange(node.id, styleWithUpdatedEffect(node.style, pickerTarget.effectId, { color: paint.color }));
                  } else if (paint.type === "linearGradient" || paint.type === "radialGradient") {
                    onStyleChange(node.id, styleWithUpdatedEffect(node.style, pickerTarget.effectId, { color: colorAtGradientPosition(paint, 0) }));
                  }
                }
              : selectedText && resolvedPickerPaintTarget?.type === "fill"
              ? paint => onTextStylePatch(selectedText.id, { fill: paint })
              : paint => pickerPaintTarget ? onStyleChange(node.id, styleWithPaint(node.style, pickerPaintTarget, paint)) : undefined
          }
        />
      ) : null}

      {node && stylePickerKind ? (
        <StylePickerPopover
          document={document}
          kind={stylePickerKind}
          onSelect={styleId => {
            onApplyDocumentStyle(node.id, styleId);
            setStylePickerKind(undefined);
          }}
          onCreate={() => {
            setCreateStyleKind(stylePickerKind);
          }}
          onEdit={styleId => setEditingStyleId(styleId)}
          onClose={() => setStylePickerKind(undefined)}
        />
      ) : null}

      {editingStyleId ? (
        <StyleEditorPopover
          document={document}
          styleId={editingStyleId}
          activeColorSamplerSourceId={activeColorSamplerSourceId}
          onStyleChange={onUpdateDocumentStyle}
          onStyleDelete={styleId => {
            onDeleteDocumentStyle(styleId);
            setEditingStyleId(undefined);
            if (stylePickerKind) {
              setStylePickerKind(undefined);
            }
          }}
          onColorSampleRequest={onColorSampleRequest}
          onColorSampleModeChange={onColorSampleModeChange}
          onColorSampleCancel={onColorSampleCancel}
          onClose={() => setEditingStyleId(undefined)}
        />
      ) : null}

      {node && createStyleKind ? (
        <CreateStyleDialog
          kind={createStyleKind}
          onCancel={() => setCreateStyleKind(undefined)}
          onConfirm={name => {
            const textRange = selectedText
              ? textInspectorRange(selectedText.text, textEditSelection)
              : undefined;
            const options = createStyleKind === "text"
              ? { textRange }
              : createStyleOptions;
            onCreateDocumentStyle(node.id, createStyleKind, name, options);
            setCreateStyleKind(undefined);
            setStylePickerKind(undefined);
          }}
        />
      ) : null}
    </aside>
  );

  function openStylePicker(kind: DocumentStyleKind, options: CreateStyleFromSelectionOptions = {}): void {
    setCreateStyleOptions(options);
    setStylePickerKind(kind);
  }

  function commitBounds(bounds: Rect): void {
    const documentBounds = documentBoundsFromInspectorBounds(bounds, boundsFrameContext);
    if (selectedNodeIds.length > 1) {
      onSelectionBoundsChange(documentBounds);
      return;
    }

    if (node) {
      onBoundsChange(node.id, documentBounds);
    }
  }
}

function sharedRotation(nodes: SceneNode[]): number | undefined {
  if (nodes.length === 0) {
    return undefined;
  }

  const first = nodes[0]!.transform.rotation;
  return nodes.every(node => node.transform.rotation === first) ? first : undefined;
}

export function sharedOpacity(nodes: SceneNode[]): number | "mixed" | undefined {
  if (nodes.length === 0) {
    return undefined;
  }

  const first = nodes[0]!.style.opacity;
  return nodes.every(node => node.style.opacity === first) ? first : "mixed";
}

function PrototypePanel({
  selectedFrame,
  outgoingOnlyFrames,
  onLocateNode,
  onPlayPrototype,
}: {
  selectedFrame?: FrameNode;
  outgoingOnlyFrames: FrameNode[];
  onLocateNode: (nodeId: string) => void;
  onPlayPrototype: (frameId: string) => void;
}) {
  return (
    <div className={`flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto overscroll-contain p-3 ${INSPECTOR_SECTIONS_CLASS}`}>
      {selectedFrame ? (
        <PanelSection
          title="Selected Frame"
          headerRight={(
            <button
              type="button"
              aria-label={`Play ${selectedFrame.name}`}
              title={`Play ${selectedFrame.name}`}
              onClick={() => onPlayPrototype(selectedFrame.id)}
              className="grid size-6 place-items-center rounded text-slate-400 transition-colors hover:bg-white/10 hover:text-sky-100"
            >
              <Play className="size-3.5" aria-hidden="true" />
            </button>
          )}
        >
          <div className="truncate rounded bg-white/[0.04] px-2 py-1.5 text-slate-200">{selectedFrame.name}</div>
        </PanelSection>
      ) : null}

      <PanelSection title="Starting Frames">
        {outgoingOnlyFrames.length === 0 ? (
          <div className="rounded bg-white/[0.03] px-2 py-2 text-slate-500">No starting frames</div>
        ) : (
          <div className="grid gap-1">
            {outgoingOnlyFrames.map(frame => (
              <div key={frame.id} className="flex h-8 items-center gap-2 rounded bg-white/[0.04] px-2 text-slate-300">
                <span className="min-w-0 flex-1 truncate">{frame.name}</span>
                <button
                  type="button"
                  aria-label={`Locate ${frame.name}`}
                  title={`Locate ${frame.name}`}
                  onClick={() => onLocateNode(frame.id)}
                  className="grid size-6 place-items-center rounded text-slate-500 transition-colors hover:bg-white/10 hover:text-slate-100"
                >
                  <LocateFixed className="size-3.5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  aria-label={`Play ${frame.name}`}
                  title={`Play ${frame.name}`}
                  onClick={() => onPlayPrototype(frame.id)}
                  className="grid size-6 place-items-center rounded text-slate-500 transition-colors hover:bg-white/10 hover:text-sky-100"
                >
                  <Play className="size-3.5" aria-hidden="true" />
                </button>
              </div>
            ))}
          </div>
        )}
      </PanelSection>
    </div>
  );
}

type ColorPickerTarget = PaintTarget | { type: "effectColor"; effectId: string };

function isPaintPickerTarget(target: ColorPickerTarget | undefined): target is PaintTarget {
  return target === "fill" || target === "stroke" || (typeof target === "object" && (target.type === "fill" || target.type === "stroke"));
}

function isEffectPickerTarget(target: ColorPickerTarget | undefined): target is { type: "effectColor"; effectId: string } {
  return typeof target === "object" && target.type === "effectColor";
}

function VectorSection({
  vertices,
  onChange,
}: {
  vertices: Array<{ id: string; handleMode?: VectorHandleMode; strokeJoin?: VectorStrokeJoin; cornerRadius?: number }>;
  onChange: (patch: { handleMode?: VectorHandleMode; strokeJoin?: VectorStrokeJoin; cornerRadius?: number }) => void;
}) {
  const handleMode = vertices[0]?.handleMode ?? "none";
  const strokeJoin = vertices[0]?.strokeJoin ?? "miter";
  const firstRadius = vertices[0]?.cornerRadius ?? 0;
  const radius = vertices.every(vertex => (vertex.cornerRadius ?? 0) === firstRadius) ? firstRadius : undefined;

  return (
    <PanelSection title="Vector">
      <div className="grid gap-3">
        <SegmentedControl
          label="Handles"
          value={handleMode}
          options={[
            { value: "none", label: "Free" },
            { value: "mirrorAngle", label: "Angle" },
            { value: "mirrorAngleAndLength", label: "Angle + length" },
          ]}
          onChange={value => onChange({ handleMode: value as VectorHandleMode })}
        />
        <SegmentedControl
          label="Join"
          value={strokeJoin}
          options={[
            { value: "miter", label: "Miter" },
            { value: "bevel", label: "Bevel" },
            { value: "round", label: "Round" },
          ]}
          onChange={value => onChange({ strokeJoin: value as VectorStrokeJoin })}
        />
        <Metric
          label="R"
          value={radius}
          min={0}
          disabled={false}
          onCommit={cornerRadius => onChange({ cornerRadius })}
        />
      </div>
    </PanelSection>
  );
}

function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}) {
  return (
    <div>
      <div className="mb-1 text-[11px] text-slate-500">{label}</div>
      <div className="grid grid-cols-3 rounded bg-white/[0.04] p-0.5">
        {options.map(option => (
          <button
            key={option.value}
            type="button"
            onClick={() => onChange(option.value)}
            className={`min-h-7 rounded px-1 text-[11px] leading-tight transition-colors ${
              value === option.value ? "bg-sky-500/25 text-sky-100" : "text-slate-400 hover:bg-white/[0.07] hover:text-slate-200"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function PaintStackSection({
  title,
  empty,
  children,
  onAdd,
  onOpenStyles,
  appliedStyle,
}: {
  title: string;
  empty: boolean;
  children: ReactNode;
  onAdd: () => void;
  onOpenStyles?: () => void;
  appliedStyle?: ReactNode;
}) {
  return (
    <PanelSection
      title={title}
      headerRight={(
        <PanelSectionHeaderActions
          styleLabel={`Open ${title.toLowerCase()} styles`}
          onOpenStyles={onOpenStyles}
          addLabel={`Add ${title.toLowerCase()}`}
          onAdd={onAdd}
        />
      )}
    >
      {appliedStyle ? appliedStyle : empty ? null : <div className="grid min-w-0 gap-2">{children}</div>}
    </PanelSection>
  );
}

interface RectangleSectionProps {
  node: RectangleNode;
  onPrimitiveChange: (nodeId: string, primitive: PrimitiveDefinition) => void;
}

type RectangleNode = PrimitiveNode & { primitive: RectanglePrimitive };

function isRectangleNode(node: SceneNode | undefined): node is RectangleNode {
  return node?.type === "primitive" && node.primitive.kind === "rectangle";
}

type OvalNode = PrimitiveNode & { primitive: OvalPrimitive };
function isOvalNode(node: SceneNode | undefined): node is OvalNode {
  return node?.type === "primitive" && node.primitive.kind === "oval";
}

type PolygonNodeType = PrimitiveNode & { primitive: PolygonPrimitive };
function isPolygonNode(node: SceneNode | undefined): node is PolygonNodeType {
  return node?.type === "primitive" && node.primitive.kind === "polygon";
}

type StarNodeType = PrimitiveNode & { primitive: StarPrimitive };
function isStarNode(node: SceneNode | undefined): node is StarNodeType {
  return node?.type === "primitive" && node.primitive.kind === "star";
}

// ─── Oval section ────────────────────────────────────────────────────────────

interface OvalSectionProps {
  node: OvalNode;
  onPrimitiveChange: (nodeId: string, primitive: PrimitiveDefinition) => void;
}

function OvalSection({ node, onPrimitiveChange }: OvalSectionProps) {
  const prim = node.primitive;
  const arcStart = prim.arcStart ?? 0;
  const arcSweep = prim.arcSweep ?? 360;
  const ratio     = prim.ratio ?? 0;

  function commit(patch: Partial<OvalPrimitive>): void {
    onPrimitiveChange(node.id, { ...prim, ...patch });
  }

  return (
    <PanelSection title="Arc">
      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="Start"
          value={arcStart}
          disabled={false}
          min={0}
          max={360}
          onCommit={value => commit({ arcStart: ((value % 360) + 360) % 360 })}
        />
        <Metric
          label="Sweep"
          value={arcSweep}
          disabled={false}
          min={1}
          max={360}
          onCommit={value => commit({ arcSweep: Math.max(1, Math.min(360, value)) })}
        />
        <Metric
          label="Ratio"
          value={Math.round(ratio * 100)}
          disabled={false}
          min={0}
          max={99}
          onCommit={value => commit({ ratio: Math.max(0, Math.min(0.99, value / 100)) })}
        />
      </div>
    </PanelSection>
  );
}

// ─── Polygon section ──────────────────────────────────────────────────────────

interface PolygonSectionProps {
  node: PolygonNodeType;
  onPrimitiveChange: (nodeId: string, primitive: PrimitiveDefinition) => void;
}

function PolygonSection({ node, onPrimitiveChange }: PolygonSectionProps) {
  const prim = node.primitive;
  const curvature = prim.curvature ?? 0;

  function commit(patch: Partial<PolygonPrimitive>): void {
    onPrimitiveChange(node.id, { ...prim, ...patch });
  }

  return (
    <PanelSection title="Polygon">
      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="Count"
          value={prim.sides}
          disabled={false}
          min={3}
          max={64}
          onCommit={value => commit({ sides: Math.max(3, Math.round(value)) })}
        />
        <Metric
          label="Radius"
          value={Math.round(curvature * 100) / 100}
          disabled={false}
          min={0}
          max={1}
          onCommit={value => commit({ curvature: Math.max(0, Math.min(1, value)) })}
        />
      </div>
    </PanelSection>
  );
}

// ─── Star section ─────────────────────────────────────────────────────────────

interface StarSectionProps {
  node: StarNodeType;
  onPrimitiveChange: (nodeId: string, primitive: PrimitiveDefinition) => void;
}

function StarSection({ node, onPrimitiveChange }: StarSectionProps) {
  const prim = node.primitive;

  function commit(patch: Partial<StarPrimitive>): void {
    onPrimitiveChange(node.id, { ...prim, ...patch });
  }

  return (
    <PanelSection title="Star">
      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="Count"
          value={prim.points}
          disabled={false}
          min={3}
          max={64}
          onCommit={value => commit({ points: Math.max(3, Math.round(value)) })}
        />
        <Metric
          label="Ratio"
          value={Math.round(prim.innerRadiusRatio * 100)}
          disabled={false}
          min={5}
          max={95}
          onCommit={value => commit({ innerRadiusRatio: Math.max(0.05, Math.min(0.95, value / 100)) })}
        />
      </div>
    </PanelSection>
  );
}

function directParentFlexFrame(document: CurvaDocument, nodeId: string): FrameNode | undefined {
  for (const page of document.pages) {
    const parent = directParentFlexFrameInNodes(page.children, nodeId);
    if (parent) {
      return parent;
    }
  }
  return undefined;
}

function directParentFlexFrameInNodes(nodes: SceneNode[], nodeId: string): FrameNode | undefined {
  for (const node of nodes) {
    const children = getSceneNodeChildren(node);
    if (!children) {
      continue;
    }

    if (node.type === "frame" && frameAutoLayoutMode(node) === "flex" && children.some(child => child.id === nodeId)) {
      return node;
    }

    const nested = directParentFlexFrameInNodes(children, nodeId);
    if (nested) {
      return nested;
    }
  }
  return undefined;
}

function RectangleSection({ node, onPrimitiveChange }: RectangleSectionProps) {
  const radii = node.primitive.cornerRadii;
  const allValue = radii.every(radius => radius === radii[0]) ? radii[0] : undefined;
  const maxRadius = Math.min(Math.abs(node.bounds.width), Math.abs(node.bounds.height)) / 2;

  function commitRadii(nextRadii: [number, number, number, number]): void {
    onPrimitiveChange(node.id, {
      ...node.primitive,
      cornerRadii: nextRadii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number],
    });
  }

  return (
    <PanelSection title="Corner">
      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="All"
          value={allValue}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([value, value, value, value])}
        />
        <Metric
          label="TL"
          value={radii[0]}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([value, radii[1], radii[2], radii[3]])}
        />
        <Metric
          label="TR"
          value={radii[1]}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([radii[0], value, radii[2], radii[3]])}
        />
        <Metric
          label="BR"
          value={radii[2]}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([radii[0], radii[1], value, radii[3]])}
        />
        <Metric
          label="BL"
          value={radii[3]}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([radii[0], radii[1], radii[2], value])}
        />
      </div>
    </PanelSection>
  );
}

function ComponentSection({
  component,
  onAddVariant,
  onDeleteVariant,
}: {
  component: ComponentNode;
  onAddVariant: (componentId: string) => void;
  onDeleteVariant: (componentId: string, variantId: string) => void;
}) {
  const variants = componentVariants(component);
  return (
    <PanelSection
      title="Variants"
      headerRight={<PanelSectionAddButton label="Add variant" onClick={() => onAddVariant(component.id)} />}
    >
      <div className="grid gap-1">
        {variants.map(variant => (
          <div key={variant.id} className="flex h-8 items-center gap-2 rounded bg-white/[0.04] px-2 text-slate-300">
            <span className="h-2 w-2 rotate-45 rounded-[1px] bg-violet-400" aria-hidden="true" />
            <span className="min-w-0 flex-1 truncate">{variant.name}</span>
            <button
              type="button"
              className="rounded px-1.5 py-0.5 text-[11px] text-slate-500 hover:bg-white/10 hover:text-slate-200 disabled:pointer-events-none disabled:opacity-35"
              disabled={variants.length <= 1}
              onClick={() => onDeleteVariant(component.id, variant.id)}
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </PanelSection>
  );
}

function InstanceSection({
  instance,
  component,
  onVariantChange,
}: {
  instance: ComponentInstanceNode;
  component: ComponentNode;
  onVariantChange: (instanceId: string, variantId: string) => void;
}) {
  const variants = componentVariants(component);
  return (
    <PanelSection title="Instance">
      <PanelLabeledSelect
        label="Variant"
        value={instance.variantId}
        options={variants.map(variant => ({ value: variant.id, label: variant.name }))}
        onChange={variantId => onVariantChange(instance.id, variantId)}
      />
    </PanelSection>
  );
}

interface FrameSectionProps {
  frame: FrameNode;
  onClipsContentChange: (nodeId: string, clipsContent: boolean) => void;
  onCornerRadiiChange: (nodeId: string, cornerRadii: [number, number, number, number]) => void;
}

function FrameSection({ frame, onClipsContentChange, onCornerRadiiChange }: FrameSectionProps) {
  const radii = frameCornerRadii(frame);
  const allValue = radii.every(radius => radius === radii[0]) ? radii[0] : undefined;
  const maxRadius = Math.min(Math.abs(frame.bounds.width), Math.abs(frame.bounds.height)) / 2;

  function commitRadii(nextRadii: [number, number, number, number]): void {
    onCornerRadiiChange(
      frame.id,
      nextRadii.map(radius => Math.max(0, Math.min(maxRadius, radius))) as [number, number, number, number],
    );
  }

  return (
    <PanelSection title="Frame">
      <div className="grid grid-cols-2 gap-2">
        <Metric
          label="All"
          value={allValue}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([value, value, value, value])}
        />
        <Metric
          label="TL"
          value={radii[0]}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([value, radii[1], radii[2], radii[3]])}
        />
        <Metric
          label="TR"
          value={radii[1]}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([radii[0], value, radii[2], radii[3]])}
        />
        <Metric
          label="BR"
          value={radii[2]}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([radii[0], radii[1], value, radii[3]])}
        />
        <Metric
          label="BL"
          value={radii[3]}
          disabled={false}
          min={0}
          max={maxRadius}
          onCommit={value => commitRadii([radii[0], radii[1], radii[2], value])}
        />
      </div>
      <label className="flex mt-2 h-8 cursor-pointer items-center justify-between rounded bg-white/[0.04] px-2 text-slate-300">
        <span>Clip content</span>
        <input
          type="checkbox"
          checked={frame.clipsContent}
          onChange={event => onClipsContentChange(frame.id, event.currentTarget.checked)}
          className="h-4 w-4 accent-sky-500"
        />
      </label>
    </PanelSection>
  );
}
